import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { findRoots } from '../../../analytics/numerical-methods';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { RootsParams } from './roots.model';

/**
 * Creates root points (x-intercepts) for any function type.
 * Uses numerical root-finding (bisection) to find where f(x) = 0.
 *
 * JSXGraph can find intersections between two objects (for roots we would create an invisible
 * x axis and find the intersection).
 *
 * However, JSXGraph's intersection requires a fixed index (0, 1, 2...) to specify which
 * intersection, it doesn't dynamically find all intersections.
 *
 * The currently custom implementation does the following:
 * - Dynamically finds all roots in the visible bounding box
 * - Adapts to viewport changes (roots outside view aren't computed)
 * - Handles any number of roots up to maxRoots
 */

export class RootsCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'roots';

  private readonly maxRoots = 10;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as RootsParams | undefined;
    if (!params?.of) {
      throw new Error('Roots feature requires params.of to specify the parent function');
    }
    // Use getEvaluableParent to support both functions and derivatives
    const parentFn = this.getEvaluableParent(params.of, context);
    const objects: GeometryElement[] = [];

    const style = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    for (let i = 0; i < this.maxRoots; i++) {
      const result = elementFactory.point({
        coords: [
          () => {
            const bbox = board.getBoundingBox();
            const roots = findRoots(parentFn.evaluate, bbox[0], bbox[2], { maxResults: this.maxRoots });
            return i < roots.length ? roots[i] : NaN;
          },
          0,
        ],
        name: 'root',
        style: { ...style, visible },
      });
      objects.push(result.point);
      if (result.label) {
        objects.push(result.label);
      }
    }

    return {
      id,
      type: 'roots',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
